// SimilarWeb API integration
// This module handles website analytics and traffic data fetching

import axios from 'axios';
import { similarwebConfig } from '../../../config/api.config';
import { OutreachCategory } from '../../../types/outreach';
import type { MusicBlog } from '../../../types/outreach';
import { getOutreachItem, updateOutreachItem } from '../firestore/crud';

/**
 * Extract domain from URL
 *
 * @param url URL to extract domain from
 * @returns Domain or null if not found
 */
const extractDomain = (url: string): string | null => {
  try {
    console.log("Extracting domain from URL:", url);

    // Handle URLs without protocol
    if (!url.startsWith("http")) {
      url = "https://" + url;
    }

    const urlObj = new URL(url);
    const hostname = urlObj.hostname;

    // Remove www. prefix if present
    const domain = hostname.replace(/^www\./, "");

    console.log("Extracted domain:", domain);
    return domain;
  } catch (error) {
    console.error("Error extracting domain:", error);
    return null;
  }
};

/**
 * Fetch Similarweb analytics for a domain
 *
 * @param domain Domain to fetch analytics for
 * @param apiKey Similarweb API key
 * @returns Analytics data or null if not found
 */
export const fetchSimilarwebAnalytics = async (
  domain: string,
  apiKey: string = similarwebConfig.apiKey
): Promise<{ monthlyVisits: number; category?: string } | null> => {
  try {
    console.log("Fetching Similarweb analytics for domain:", domain);

    // Get monthly visits
    const visitsResponse = await axios.get(
      `https://api.similarweb.com/v1/website/${domain}/total-traffic-and-engagement/visits`,
      {
        params: {
          api_key: apiKey,
          start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0], // 30 days ago
          end_date: new Date().toISOString().split("T")[0], // today
          country: "world",
          granularity: "monthly",
        },
      }
    );

    console.log("Similarweb visits response:", visitsResponse.data);

    if (!visitsResponse.data || !visitsResponse.data.visits) {
      console.error("No visits data found for domain:", domain);
      return null;
    }

    // Get the most recent month's visits
    const visits = visitsResponse.data.visits;
    const lastMonthVisits = visits[visits.length - 1].visits;

    // Get category information
    let category = undefined;
    try {
      const categoryResponse = await axios.get(
        `https://api.similarweb.com/v1/website/${domain}/category`,
        {
          params: {
            api_key: apiKey,
          },
        }
      );

      if (categoryResponse.data && categoryResponse.data.category) {
        category = categoryResponse.data.category;
      }
    } catch (categoryError) {
      console.error(
        "Error fetching category for domain:",
        domain,
        categoryError
      );
      // Continue without category data
    }

    const result = {
      monthlyVisits: Math.round(lastMonthVisits),
      category,
    };

    console.log("Successfully fetched Similarweb analytics:", result);
    return result;
  } catch (error) {
    console.error("Error fetching Similarweb analytics:", error);
    return null;
  }
};

/**
 * Update music blog analytics
 *
 * @param blogId Blog ID to update
 * @param apiKey Similarweb API key
 * @returns True if successful, false otherwise
 */
export const updateMusicBlogAnalytics = async (
  blogId: string,
  apiKey: string = similarwebConfig.apiKey
): Promise<boolean> => {
  try {
    console.log(`Updating analytics for music blog ID: ${blogId}`);

    // Get the blog
    const blog = await getOutreachItem<MusicBlog>(
      OutreachCategory.MUSIC_BLOGS,
      blogId
    );

    if (!blog || !blog.blogUrl) {
      console.error("Blog not found or missing URL");
      return false;
    }

    console.log(
      `Fetching analytics for blog: ${blog.blogName} (${blog.blogUrl})`
    );

    // Extract domain from URL
    const domain = extractDomain(blog.blogUrl);

    if (!domain) {
      console.error("Could not extract domain from URL:", blog.blogUrl);
      return false;
    }

    // Fetch analytics
    const analytics = await fetchSimilarwebAnalytics(domain, apiKey);

    if (!analytics) {
      console.error("Failed to fetch analytics for blog:", blog.blogName);
      return false;
    }

    console.log(
      `Successfully fetched analytics for ${blog.blogName}:`,
      analytics
    );

    // Update the blog with new data
    const updateData: Partial<MusicBlog> = {
      monthlyVisits: analytics.monthlyVisits,
      lastUpdated: new Date().toISOString(),
    };

    // If category is available and maps to a genre, update the genre
    if (analytics.category) {
      // Map category to genre if possible
      const categoryToGenre: Record<string, string> = {
        "Arts_and_Entertainment/Music": "Any",
        "Arts_and_Entertainment/Music_and_Audio": "Any",
        "Arts_and_Entertainment/Music_Reference": "Any",
        "Arts_and_Entertainment/Music_Streams_and_Downloads": "Any",
        // Add more mappings as needed
      };

      if (categoryToGenre[analytics.category]) {
        updateData.genre = categoryToGenre[analytics.category];
      }
    }

    console.log(`Updating blog ${blogId} with data:`, updateData);

    await updateOutreachItem<MusicBlog>(
      OutreachCategory.MUSIC_BLOGS,
      blogId,
      updateData
    );

    console.log(`Successfully updated analytics for ${blog.blogName}`);
    return true;
  } catch (error) {
    console.error("Error updating music blog analytics:", error);
    return false;
  }
};